import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  fetchManualOrders,
  deleteManualOrder,
  submitManualOrder,
  clearManualOrderError,
  clearManualOrderSuccess,
  selectManualDrafts,
  selectManualPagination,
  selectManualLoading,
  selectManualSubmitting,
  selectManualError,
  selectManualSuccess,
} from "../../../slice/manual/manualorderslice";

export default function ManualOrderList() {
  const dispatch  = useDispatch();
  const navigate  = useNavigate();

  const drafts     = useSelector(selectManualDrafts);
  const pagination = useSelector(selectManualPagination);
  const loading    = useSelector(selectManualLoading);
  const submitting = useSelector(selectManualSubmitting);
  const error      = useSelector(selectManualError);
  const successMsg = useSelector(selectManualSuccess);

  const [page, setPage]             = useState(1);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [search, setSearch]         = useState("");
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    dispatch(fetchManualOrders({ page, showSubmitted }));
  }, [dispatch, page, showSubmitted]);

  useEffect(() => {
    if (successMsg) { toast.success(successMsg); dispatch(clearManualOrderSuccess()); }
  }, [successMsg, dispatch]);

  useEffect(() => {
    if (error) { toast.error(error); dispatch(clearManualOrderError()); }
  }, [error, dispatch]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this draft?")) return;
    setDeletingId(id);
    await dispatch(deleteManualOrder(id));
    setDeletingId(null);
  };

  const handleSubmit = async (draft) => {
    const missing = [];
    if (!draft.fullName?.trim())      missing.push("Full Name");
    if (!draft.phoneNumber?.trim())   missing.push("Phone Number");
    if (!draft.streetAddress?.trim()) missing.push("Street Address");
    if (!draft.pinCode?.trim())       missing.push("PIN Code");
    if (!draft.district?.trim())      missing.push("District");
    if (!draft.state?.trim())         missing.push("State");
    if (!draft.product)               missing.push("Product");
    if (!draft.totalAmount || draft.totalAmount <= 0) missing.push("Total Amount");

    if (missing.length > 0) {
      toast.error(`Missing: ${missing.join(", ")}. Please edit the draft first.`);
      return;
    }
    if (!window.confirm("Submit this order to Order Received?")) return;
    const result = await dispatch(submitManualOrder(draft._id));
    if (result.meta.requestStatus === "fulfilled") {
      navigate("/admin/order/Received");
    }
  };

  /* Client-side search filter */
  const filtered = drafts.filter((d) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      d.fullName?.toLowerCase().includes(q)       ||
      d.phoneNumber?.includes(q)                  ||
      d.product?.name?.toLowerCase().includes(q)  ||
      d.pinCode?.includes(q)                      ||
      d.district?.toLowerCase().includes(q)       ||
      d.state?.toLowerCase().includes(q)
    );
  });

  if (loading) return <p className="text-center mt-4">Loading…</p>;

  return (
    <div className="row">
      <div className="col-12">
        <div className="card mt-3">

          {/* Header */}
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="card-title mb-0">
                {showSubmitted ? "Submitted Orders" : "Manual Order Drafts"}
              </h4>
              <small className="text-muted">{pagination.total ?? 0} total</small>
            </div>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {/* Search */}
              <div className="input-group input-group-sm" style={{ width: 220 }}>
                <span className="input-group-text bg-white">
                  <i className="bx bx-search" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setSearch("")}
                  >✕</button>
                )}
              </div>

              {/* Toggle */}
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showSubmittedSwitch"
                  checked={showSubmitted}
                  onChange={(e) => {
                    setShowSubmitted(e.target.checked);
                    setPage(1);
                    setSearch("");
                  }}
                />
                <label className="form-check-label" htmlFor="showSubmittedSwitch">
                  Show Submitted
                </label>
              </div>

              <button
                className="btn cusBTNPop btn-sm"
                onClick={() => navigate("/admin/manual-order/create")}
              >
                + New Order
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th>Si</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Items</th>
                  <th>Amount</th>
                  <th>Paid</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length > 0 ? (
                  filtered.map((d, i) => (
                    <tr key={d._id}>
                      <td>{(page - 1) * 20 + i + 1}</td>
                      <td>
                        <strong>{d.fullName}</strong><br />
                        <small className="text-muted">{d.phoneNumber}</small><br />
                        <small className="text-muted">
                          {d.district}, {d.state} — {d.pinCode}
                        </small>
                      </td>
                      <td>{d.product?.name || "—"}</td>
                      <td className="text-center">{d.noOfItems}</td>
                      <td>₹{d.totalAmount}</td>
                      <td>
                        <span className={`badge ${d.paid ? "bg-success" : "bg-danger"}`}>
                          {d.paid ? "Yes" : "No"}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${d.draftStatus === "submitted" ? "bg-primary" : "bg-warning text-dark"}`}>
                          {d.draftStatus === "submitted" ? "Submitted" : "Draft"}
                        </span>
                      </td>
                      <td>{new Date(d.createdAt).toLocaleDateString("en-IN")}</td>
                      <td>
                        <div className="d-flex gap-1 flex-wrap">
                          {d.draftStatus === "draft" && (
                            <>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => navigate(`/admin/manual-order/edit/${d._id}`)}
                              >
                                   <iconify-icon icon="solar:pen-2-broken" />
                              </button>
                          
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDelete(d._id)}
                                disabled={deletingId === d._id}
                              >
                                    <iconify-icon icon="solar:trash-bin-minimalistic-2-broken" />
                              </button>
                                  <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleSubmit(d)}
                                disabled={submitting}
                              >
                                🚀 Submit
                              </button>
                            </>
                          )}
                          {d.draftStatus === "submitted" && (
                            <span className="text-muted small">Moved to Orders</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center text-muted py-4">
                      {search
                        ? `No results for "${search}"`
                        : `No ${showSubmitted ? "submitted" : "draft"} orders found`
                      }
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="card-footer d-flex justify-content-center gap-2">
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >‹ Prev</button>
              <span className="align-self-center text-muted">
                Page {page} of {pagination.totalPages}
              </span>
              <button
                className="btn btn-sm btn-outline-secondary"
                disabled={page === pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >Next ›</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}